import { useNavigate } from 'react-router-dom';

import { InfoView } from '@backrs_ui/common-ui';

import { APP_ROUTE } from '@backrs_ui/grow-ui/src/App';
import { appStrings } from '@backrs_ui/grow-ui/src/assets/appStrings';
import { useAppSelector } from '@backrs_ui/grow-ui/src/redux/hooks';

import { Box, Typography, SxProps, Theme, Button } from '@mui/material';

const BACKRS_URL = process.env.REACT_APP_BACKRS_URL;

const MARKERS_OF_SUCCESS_STRINGS = appStrings.modules.results;

export const SuccessMoreInfo = () => {
  const navigate = useNavigate();

  const { user } = useAppSelector((state) => state.user);

  const cardData: {
    imagePath: string;
    text: string;
    onClick: () => void;
  }[] = [
    {
      imagePath: '/images/stickers/AppsSoftware.png',
      text: MARKERS_OF_SUCCESS_STRINGS.moreInfo.information.dashboard,
      onClick: () => navigate(APP_ROUTE.HOME),
    },
    {
      imagePath: '/images/stickers/ScienceDiagram.png',
      text: MARKERS_OF_SUCCESS_STRINGS.moreInfo.information.networks,
      onClick: () => window.open(`${BACKRS_URL}/tabs/feed`, '_blank'),
    },
    {
      imagePath: '/images/stickers/Weight.png',
      text: MARKERS_OF_SUCCESS_STRINGS.moreInfo.information.lifeReadiness,
      onClick: () => navigate(APP_ROUTE.LIFE_READINESS),
    },
  ];

  return (
    <Box sx={successMoreInfoStyle}>
      <InfoView
        icon="Hash01"
        headerContent={
          <Box sx={contentContainerSx}>
            <Typography
              variant="text-lg-semibold"
              color={({ backrs }) => backrs.colors['Colors/Text/text-primary']}
            >
              {MARKERS_OF_SUCCESS_STRINGS.moreInfo.title}
            </Typography>
            <Typography
              variant="text-sm-regular"
              color={({ backrs }) => backrs.colors['Colors/Text/text-tertiary']}
            >
              {MARKERS_OF_SUCCESS_STRINGS.moreInfo.forSupport}

              <Typography
                variant="text-sm-regular"
                component="span"
                sx={backrsLinkSx}
                onClick={() => navigate(user?.access_token ? APP_ROUTE.HOME : APP_ROUTE.REGISTER)}
              >
                {MARKERS_OF_SUCCESS_STRINGS.moreInfo.forSupportLink}
              </Typography>
            </Typography>
          </Box>
        }
        bodyContent={
          <Box sx={contentContainerSx}>
            <Box sx={cardsContainerSx}>
              {cardData.map((card) => (
                <Box key={card.text} sx={cardContainerStyle} onClick={card.onClick}>
                  <Box component="img" src={card.imagePath} />
                  <Typography
                    variant="text-sm-medium"
                    color={({ backrs }) => backrs.colors['Colors/Text/text-secondary']}
                  >
                    {card.text}
                  </Typography>
                </Box>
              ))}
            </Box>
            <Button
              variant="contained"
              hierarchy="Primary"
              size="lg"
              onClick={() => navigate(user?.access_token ? APP_ROUTE.HOME : APP_ROUTE.REGISTER)}
              sx={joinBackrsButtonSx}
              fullWidth
            >
              {MARKERS_OF_SUCCESS_STRINGS.moreInfo.button}
            </Button>
          </Box>
        }
      />
    </Box>
  );
};

const contentContainerSx: SxProps<Theme> = {
  zIndex: 100,
  position: 'relative',
};

const successMoreInfoStyle: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  border: `2px solid ${backrs.colors['Colors/Base/Black']}`,
  borderRadius: backrs.radius['radius-lg'],
  height: 'fit-content',

  width: {
    xs: 'unset',
    sm: 'unset',
    md: '100%',
  },
});

const cardsContainerSx: SxProps<Theme> = ({ backrs }) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: backrs.spacing['spacing-lg'],
});

const cardContainerStyle: SxProps<Theme> = ({ backrs }) => ({
  backgroundColor: backrs.colors['Colors/Background/bg-primary'],
  borderRadius: backrs.radius['radius-lg'],
  border: `1px solid ${backrs.colors['Colors/Border/border-secondary']}`,

  cursor: 'pointer',

  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  gap: backrs.spacing['spacing-lg'],
  padding: backrs.spacing['spacing-xl'],

  img: {
    maxHeight: '72px',
    objectFit: 'contain',
  },
});

const backrsLinkSx: SxProps<Theme> = ({ backrs }) => ({
  textDecoration: 'underline',
  color: backrs.colors['Colors/Text/text-tertiary'],
  cursor: 'pointer',
});

const joinBackrsButtonSx: SxProps<Theme> = ({ backrs }) => ({
  mt: backrs.spacing['spacing-4xl'],
});
